import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, IndianRupee, ArrowUpRight, Clock, Check, X, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import {
  AdminAdjustWalletDialog,
  type AdminAdjustWalletTarget,
} from "@/components/admin/AdminAdjustWalletDialog";
import {
  useAdminSettlements,
  useAdminPayments,
  useAdminUsers,
  useFailSettlementMutation,
  useProcessSettlementMutation,
} from "@/hooks/queries";
import type { AdminPaymentRow } from "@/lib/mappers/admin-mappers";
import type { AdminSettlementRow } from "@/lib/mappers/account-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

const statusBadge: Record<string, string> = {
  completed: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  processing: "bg-primary/10 text-primary border-primary/20",
  failed: "bg-destructive/10 text-destructive border-destructive/20",
  refunded: "bg-muted text-muted-foreground border-border",
};

const paymentMethodLabel: Record<string, string> = {
  online: "Razorpay",
  razorpay: "Razorpay",
  cod: "COD",
  wallet: "Wallet",
};

const AdminWallet = () => {
  const { toast } = useToast();
  const { data, isLoading } = useAdminSettlements({ pageSize: 100 });
  const { data: paymentsData, isLoading: isLoadingPayments } = useAdminPayments({ pageSize: 100 });
  const processMutation = useProcessSettlementMutation();
  const failMutation = useFailSettlementMutation();

  const [userSearch, setUserSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(10);
  const [adjustTarget, setAdjustTarget] = useState<AdminAdjustWalletTarget | null>(null);

  const { data: usersData, isLoading: usersLoading, isFetching: usersFetching } = useAdminUsers({
    search: debouncedSearch.trim() || undefined,
    page: userPage,
    pageSize: userPageSize,
  });

  const settlements = data?.items ?? [];
  const payments = paymentsData?.items ?? [];
  const [processTarget, setProcessTarget] = useState<AdminSettlementRow | null>(null);
  const [transactionRef, setTransactionRef] = useState("");

  const stats = useMemo(() => {
    const pending = settlements.filter((s) => s.status === "pending").reduce((sum, s) => sum + s.amount, 0);
    const completed = settlements.filter((s) => s.status === "completed").reduce((sum, s) => sum + s.amount, 0);
    return { pending, completed, total: settlements.reduce((sum, s) => sum + s.amount, 0) };
  }, [settlements]);

  const settlementsTable = useDataTable<AdminSettlementRow>({
    data: settlements,
    searchKeys: ["settlementId", "period"],
  });

  const paymentsTable = useDataTable<AdminPaymentRow>({
    data: payments,
    searchKeys: ["orderNumber", "paymentId"],
  });

  const handleProcess = async () => {
    if (!processTarget || !transactionRef.trim()) return;
    try {
      await processMutation.mutateAsync({
        id: Number(processTarget.id),
        dto: { transactionReference: transactionRef.trim() },
      });
      toast({ title: "Settlement processed" });
      setProcessTarget(null);
      setTransactionRef("");
    } catch (error) {
      toast({ title: "Failed to process settlement", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleFail = async (settlement: AdminSettlementRow) => {
    try {
      await failMutation.mutateAsync({ id: Number(settlement.id), reason: "Marked failed by admin" });
      toast({ title: "Settlement marked as failed", variant: "destructive" });
    } catch (error) {
      toast({ title: "Failed to update settlement", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const runUserSearch = () => {
    setUserPage(1);
    setDebouncedSearch(userSearch);
  };

  const settlementColumns: DataTableColumn<AdminSettlementRow>[] = [
    {
      id: "settlementId",
      header: "Settlement",
      accessorKey: "settlementId",
      sortable: true,
      mobilePriority: 1,
      cell: (s) => <span className="font-medium">{s.settlementId}</span>,
    },
    {
      id: "period",
      header: "Period",
      accessorKey: "period",
      sortable: true,
      mobileLabel: "Period",
      mobilePriority: 2,
      className: "text-sm text-muted-foreground",
    },
    {
      id: "amount",
      header: "Amount",
      accessorKey: "amount",
      sortable: true,
      align: "right",
      mobileLabel: "Amount",
      mobilePriority: 3,
      cell: (s) => <span className="font-semibold">₹{s.amount.toLocaleString()}</span>,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 4,
      cell: (s) => (
        <Badge variant="outline" className={statusBadge[s.status]}>
          {s.status}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      hideOnMobile: true,
      cell: (s) => (
        <div className="flex items-center justify-center gap-1">
          {s.status === "pending" && (
            <>
              <Button size="sm" onClick={() => { setProcessTarget(s); setTransactionRef(""); }}>
                <Check className="h-3 w-3 mr-1" /> Process
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleFail(s)}>
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const paymentColumns: DataTableColumn<AdminPaymentRow>[] = [
    {
      id: "orderNumber",
      header: "Order",
      accessorKey: "orderNumber",
      sortable: true,
      mobilePriority: 1,
      cell: (p) => <span className="font-medium">{p.orderNumber}</span>,
    },
    {
      id: "paymentId",
      header: "Payment ID",
      accessorKey: "paymentId",
      sortable: true,
      mobileLabel: "Payment",
      mobilePriority: 2,
      className: "text-sm text-muted-foreground font-mono",
    },
    {
      id: "method",
      header: "Method",
      accessorKey: "method",
      sortable: true,
      mobileLabel: "Method",
      mobilePriority: 3,
      cell: (p) => paymentMethodLabel[p.method] ?? p.method,
    },
    {
      id: "amount",
      header: "Amount",
      accessorKey: "amount",
      sortable: true,
      align: "right",
      mobileLabel: "Amount",
      mobilePriority: 4,
      cell: (p) => <span className="font-semibold">₹{p.amount.toLocaleString()}</span>,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 5,
      cell: (p) => (
        <Badge variant="outline" className={statusBadge[p.status] ?? statusBadge.pending}>
          {p.status}
        </Badge>
      ),
    },
    {
      id: "date",
      header: "Date",
      accessorKey: "date",
      sortable: true,
      hideOnMobile: true,
      cell: (p) => (
        <span className="text-sm text-muted-foreground">
          {new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
        </span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallet & Payouts"
        description="Adjust user balances, track settlements, and process payouts."
      />

      <StatsGrid columns={4}>
        <CompactStatCard title="Total Settlements" value={`₹${(stats.total / 1000).toFixed(0)}K`} icon={IndianRupee} iconClassName="bg-gradient-primary" />
        <CompactStatCard title="Completed Payouts" value={`₹${(stats.completed / 1000).toFixed(0)}K`} icon={ArrowUpRight} />
        <CompactStatCard title="Pending Payouts" value={`₹${stats.pending.toLocaleString()}`} icon={Clock} />
        <CompactStatCard title="Settlement Count" value={settlements.length} icon={Wallet} />
      </StatsGrid>

      <Tabs defaultValue="adjust">
        <TabsList>
          <TabsTrigger value="adjust">Adjust Balance</TabsTrigger>
          <TabsTrigger value="settlements">Settlements</TabsTrigger>
                        <TabsTrigger value="transactions">Payment Txns</TabsTrigger>
        </TabsList>

        <TabsContent value="adjust" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User wallet adjust</CardTitle>
              <CardDescription>
                Browse users with server-side pagination. Missing wallets are created automatically when you adjust or open balance.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="h-11 pl-9"
                    placeholder="Search by name, email, or phone…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runUserSearch()}
                  />
                </div>
                <Button className="h-11" onClick={runUserSearch}>
                  Search
                </Button>
              </div>

              {usersLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 rounded-lg" />
                  ))}
                </div>
              ) : (usersData?.items?.length ?? 0) === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {debouncedSearch ? "No users found." : "No users on this page."}
                </p>
              ) : (
                <div className={usersFetching ? "space-y-2 opacity-60" : "space-y-2"}>
                  {usersData!.items.map((user) => {
                    const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
                    return (
                      <div
                        key={user.id}
                        className="flex flex-col gap-3 rounded-xl border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold">{name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            {user.memberCode && <span className="font-mono">{user.memberCode}</span>}
                            {user.hasWallet ? (
                              <Badge variant="outline" className="text-[10px]">
                                ₹{(user.availableBalance ?? 0).toFixed(0)} available
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px]">No wallet yet</Badge>
                            )}
                          </div>
                        </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() =>
                              setAdjustTarget({
                                userId: user.id,
                                name,
                                email: user.email,
                                memberCode: user.memberCode,
                              })
                            }
                          >
                            <Wallet className="h-3.5 w-3.5" /> Adjust Wallet
                          </Button>
                          <Button size="sm" variant="ghost" className="gap-1" asChild>
                            <Link to={`/admin/wallet/users/${user.id}`}>
                              View ledger
                            </Link>
                          </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  Showing {usersData?.items?.length ? ((userPage - 1) * userPageSize) + 1 : 0}
                  –{Math.min(userPage * userPageSize, usersData?.totalItems ?? 0)} of {usersData?.totalItems ?? 0}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="h-9 rounded-md border border-border bg-background px-2 text-xs"
                    value={userPageSize}
                    onChange={(e) => {
                      setUserPageSize(Number(e.target.value));
                      setUserPage(1);
                    }}
                  >
                    {[10, 20, 50].map((size) => (
                      <option key={size} value={size}>{size} / page</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={userPage <= 1 || usersFetching}
                    onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Page {userPage} / {Math.max(1, usersData?.totalPages ?? 1)}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={userPage >= (usersData?.totalPages ?? 1) || usersFetching}
                    onClick={() => setUserPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settlements" className="mt-4">
          <DataTable
            title="Settlement Queue"
            columns={settlementColumns}
            data={settlementsTable.rows}
            getRowId={(row) => row.id}
            search={settlementsTable.state.search}
            onSearchChange={settlementsTable.setSearch}
            searchPlaceholder="Search settlements..."
            sort={settlementsTable.state.sort}
            onSortChange={settlementsTable.toggleSort}
            pagination={settlementsTable.pagination}
            onPageChange={settlementsTable.setPage}
            onPageSizeChange={settlementsTable.setPageSize}
            emptyMessage="No settlements found."
            renderMobileCard={(s) => (
              <div className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{s.settlementId}</p>
                    <p className="text-xs text-muted-foreground">{s.period}</p>
                  </div>
                  <Badge variant="outline" className={statusBadge[s.status]}>
                    {s.status}
                  </Badge>
                </div>
                <p className="text-right font-bold">₹{s.amount.toLocaleString()}</p>
                {s.status === "pending" && (
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" onClick={() => { setProcessTarget(s); setTransactionRef(""); }}>
                      <Check className="mr-1 h-3 w-3" /> Process
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleFail(s)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          {isLoadingPayments ? (
            <Skeleton className="h-96 rounded-xl" />
          ) : (
            <DataTable
              title="Payment Transactions"
              columns={paymentColumns}
              data={paymentsTable.rows}
              getRowId={(row) => row.id}
              search={paymentsTable.state.search}
              onSearchChange={paymentsTable.setSearch}
              searchPlaceholder="Search payments..."
              sort={paymentsTable.state.sort}
              onSortChange={paymentsTable.toggleSort}
              pagination={paymentsTable.pagination}
              onPageChange={paymentsTable.setPage}
              onPageSizeChange={paymentsTable.setPageSize}
              emptyMessage="No payment transactions found."
              renderMobileCard={(p) => (
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{p.orderNumber}</p>
                      <p className="text-xs text-muted-foreground font-mono">{p.paymentId}</p>
                    </div>
                    <Badge variant="outline" className={statusBadge[p.status] ?? statusBadge.pending}>
                      {p.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>{paymentMethodLabel[p.method] ?? p.method}</span>
                    <span className="font-bold">₹{p.amount.toLocaleString()}</span>
                  </div>
                </div>
              )}
            />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(processTarget)} onOpenChange={(open) => !open && setProcessTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Settlement</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="txn-ref">Transaction Reference</Label>
            <Input
              id="txn-ref"
              value={transactionRef}
              onChange={(e) => setTransactionRef(e.target.value)}
              placeholder="Bank/UPI reference number"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessTarget(null)}>Cancel</Button>
            <Button onClick={handleProcess} disabled={!transactionRef.trim() || processMutation.isPending}>
              Confirm Payout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminAdjustWalletDialog
        open={Boolean(adjustTarget)}
        onOpenChange={(open) => !open && setAdjustTarget(null)}
        target={adjustTarget}
      />
    </div>
  );
};

export default AdminWallet;
